import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Upload, Trash2, Star, Copy, Loader2, Save, Link2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { processImage } from "@/lib/imageProcessing";

interface GalleryRow {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  event_date: string | null;
  cover_image_url: string | null;
  is_published: boolean;
  is_listed: boolean;
  is_public: boolean;
  allow_downloads: boolean;
  review_token: string | null;
  review_status: string;
  final_gallery_id: string | null;
}

interface PhotoRow {
  id: string;
  storage_path: string;
  thumbnail_path: string;
  sort_order: number;
  width: number | null;
  height: number | null;
  thumb_url?: string;
}

const AdminGalleryEditor = () => {
  const { slug } = useParams<{ slug: string }>();
  const [gallery, setGallery] = useState<GalleryRow | null>(null);
  const [photos, setPhotos] = useState<PhotoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [newPassword, setNewPassword] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    if (!slug) return;
    setLoading(true);
    const { data: g } = await supabase.from("galleries").select("*").eq("slug", slug).maybeSingle();
    if (!g) {
      toast.error("Galerie introuvable");
      setLoading(false);
      return;
    }
    setGallery(g as any);

    const { data: p } = await supabase
      .from("gallery_photos")
      .select("*")
      .eq("gallery_id", g.id)
      .order("sort_order", { ascending: true });

    const enriched = await Promise.all(
      (p || []).map(async (ph: any) => {
        const { data } = await supabase.storage
          .from("event-photos")
          .createSignedUrl(ph.thumbnail_path, 3600);
        return { ...ph, thumb_url: data?.signedUrl };
      }),
    );
    setPhotos(enriched);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [slug]);

  const handleFiles = async (files: FileList) => {
    if (!gallery) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length });

    const startOrder = photos.length;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const { thumbnail, full, width, height } = await processImage(file);
        const ts = Date.now();
        const rand = Math.random().toString(36).slice(2, 8);
        const ext = file.name.split(".").pop() || "jpg";
        const fullPath = `${gallery.id}/full/${ts}-${rand}.${ext}`;
        const thumbPath = `${gallery.id}/thumb/${ts}-${rand}.jpg`;

        const [up1, up2] = await Promise.all([
          supabase.storage.from("event-photos").upload(fullPath, full, { upsert: false }),
          supabase.storage.from("event-photos").upload(thumbPath, thumbnail, {
            upsert: false,
            contentType: "image/jpeg",
          }),
        ]);
        if (up1.error) throw up1.error;
        if (up2.error) throw up2.error;

        await supabase.from("gallery_photos").insert({
          gallery_id: gallery.id,
          storage_path: fullPath,
          thumbnail_path: thumbPath,
          width,
          height,
          file_size: file.size,
          sort_order: startOrder + i,
        });
      } catch (e: any) {
        toast.error(`${file.name}: ${e.message}`);
      }
      setProgress({ done: i + 1, total: files.length });
    }

    setUploading(false);
    toast.success("Upload terminé");
    load();
  };

  const deletePhoto = async (p: PhotoRow) => {
    if (!confirm("Supprimer cette photo ?")) return;
    await supabase.storage.from("event-photos").remove([p.storage_path, p.thumbnail_path]);
    await supabase.from("gallery_photos").delete().eq("id", p.id);
    setPhotos(photos.filter((x) => x.id !== p.id));
  };

  const setCover = async (p: PhotoRow) => {
    if (!gallery) return;
    const { data } = await supabase.storage.from("event-photos").createSignedUrl(p.storage_path, 60 * 60 * 24 * 365 * 10);
    await supabase.from("galleries").update({ cover_image_url: data?.signedUrl }).eq("id", gallery.id);
    toast.success("Photo de couverture mise à jour");
    load();
  };

  const saveSettings = async () => {
    if (!gallery) return;
    setSaving(true);
    const { error } = await supabase
      .from("galleries")
      .update({
        title: gallery.title,
        description: gallery.description,
        event_date: gallery.event_date,
        is_published: gallery.is_published,
        is_listed: gallery.is_listed,
        is_public: gallery.is_public,
        allow_downloads: gallery.allow_downloads,
      })
      .eq("id", gallery.id);
    setSaving(false);
    if (error) toast.error(error.message);
    else toast.success("Enregistré");
  };

  const updatePassword = async () => {
    if (!gallery || newPassword.length < 4) {
      toast.error("Mot de passe ≥ 4 caractères");
      return;
    }
    const { data, error } = await supabase.functions.invoke("gallery-hash-password", {
      body: { password: newPassword },
    });
    if (error || data?.error) {
      toast.error(error?.message || data?.error);
      return;
    }
    await supabase.from("galleries").update({ password_hash: data.hash }).eq("id", gallery.id);
    setNewPassword("");
    toast.success("Mot de passe mis à jour");
  };

  const copyLink = () => {
    if (!gallery) return;
    const url = `${window.location.origin}/galerie/${gallery.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien copié");
  };

  const [finalSlug, setFinalSlug] = useState<string | null>(null);
  const [rejectedIds, setRejectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!gallery?.final_gallery_id) { setFinalSlug(null); return; }
    supabase.from("galleries").select("slug").eq("id", gallery.final_gallery_id).maybeSingle()
      .then(({ data }) => setFinalSlug((data as any)?.slug || null));
  }, [gallery?.final_gallery_id]);

  useEffect(() => {
    if (!gallery?.id) return;
    supabase.from("gallery_rejections").select("photo_id").eq("gallery_id", gallery.id)
      .then(({ data }) => setRejectedIds(new Set((data || []).map((r: any) => r.photo_id))));
  }, [gallery?.id, photos.length]);

  const enableReviewMode = async () => {
    if (!gallery) return;
    const token = crypto.randomUUID().replace(/-/g, "");
    const { error } = await supabase.from("galleries").update({ review_token: token, review_status: "pending" }).eq("id", gallery.id);
    if (error) { toast.error(error.message); return; }
    setGallery({ ...gallery, review_token: token, review_status: "pending" });
    toast.success("Mode sélection activé");
  };

  const copyReviewLink = () => {
    if (!gallery?.review_token) return;
    const url = `${window.location.origin}/galerie-selection/${gallery.review_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien de sélection copié");
  };

  const reviewStatusLabel = () => {
    if (!gallery) return "";
    if (gallery.review_status === "submitted") return "Soumis";
    if (gallery.review_status === "in_progress") return `En cours — ${rejectedIds.size} retirée${rejectedIds.size > 1 ? "s" : ""}`;
    return "En attente";
  };


  if (loading) {
    return (
      <div className="text-center py-20">
        <Loader2 className="h-6 w-6 animate-spin mx-auto" />
      </div>
    );
  }
  if (!gallery) return <p>Galerie introuvable.</p>;

  return (
    <div className="space-y-6">
      <Link to="/admin/galeries" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4 mr-1" /> Retour
      </Link>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{gallery.title}</h1>
          <p className="text-sm text-muted-foreground">{photos.length} photo{photos.length > 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={copyLink}>
            <Copy className="h-4 w-4 mr-2" /> Copier le lien
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-4 space-y-4 lg:col-span-1">
          <h2 className="font-semibold">Paramètres</h2>
          <div>
            <Label>Titre</Label>
            <Input value={gallery.title} onChange={(e) => setGallery({ ...gallery, title: e.target.value })} />
          </div>
          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={gallery.event_date || ""}
              onChange={(e) => setGallery({ ...gallery, event_date: e.target.value })}
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={gallery.description || ""}
              onChange={(e) => setGallery({ ...gallery, description: e.target.value })}
            />
          </div>
          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center justify-between">
              <Label>Publiée</Label>
              <Switch
                checked={gallery.is_published}
                onCheckedChange={(v) => setGallery({ ...gallery, is_published: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Listée sur /galeries</Label>
              <Switch
                checked={gallery.is_listed}
                onCheckedChange={(v) => setGallery({ ...gallery, is_listed: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Accès public (sans mot de passe)</Label>
                <p className="text-xs text-muted-foreground">Le lien ouvre la galerie directement.</p>
              </div>
              <Switch
                checked={gallery.is_public}
                onCheckedChange={(v) => setGallery({ ...gallery, is_public: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Téléchargements</Label>
              <Switch
                checked={gallery.allow_downloads}
                onCheckedChange={(v) => setGallery({ ...gallery, allow_downloads: v })}
              />
            </div>
          </div>
          <Button onClick={saveSettings} disabled={saving} className="w-full">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Enregistrer</>}
          </Button>

          <div className="pt-4 border-t space-y-2">
            <Label>Changer le mot de passe</Label>
            <div className="flex gap-2">
              <Input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Nouveau mot de passe" />
              <Button onClick={updatePassword} variant="outline">OK</Button>
            </div>
          </div>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          <Card className="p-6 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-semibold flex items-center gap-2"><Link2 className="h-4 w-4" /> Sélection client</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Envoie un lien au client pour qu'il retire les photos qu'il ne veut pas garder.
                </p>
              </div>
              {gallery.review_token && (
                <span className="text-xs px-2 py-1 rounded-full bg-muted font-mono">{reviewStatusLabel()}</span>
              )}
            </div>
            {!gallery.review_token ? (
              <Button onClick={enableReviewMode} variant="outline" size="sm">
                Activer le mode sélection
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input readOnly value={`${window.location.origin}/galerie-selection/${gallery.review_token}`} className="font-mono text-xs" />
                  <Button onClick={copyReviewLink} variant="outline" size="sm"><Copy className="h-3 w-3" /></Button>
                </div>
                {finalSlug && (
                  <a href={`/galerie/${finalSlug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                    <ExternalLink className="h-3 w-3" /> Voir la galerie finale ({finalSlug})
                  </a>
                )}
              </div>
            )}
          </Card>

          <Card className="p-6">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => e.target.files && handleFiles(e.target.files)}
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-colors"
            >
              <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium">Cliquez pour ajouter des photos</p>
              <p className="text-xs text-muted-foreground">JPG, PNG, WebP — pleine qualité, sans limite de nombre</p>
            </div>
            {uploading && (
              <div className="mt-4">
                <Progress value={(progress.done / progress.total) * 100} />
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  {progress.done} / {progress.total}
                </p>
              </div>
            )}
          </Card>

          {photos.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {photos.map((p) => {
                const isRejected = rejectedIds.has(p.id);
                return (
                  <div key={p.id} className="relative aspect-square bg-muted rounded overflow-hidden group">
                    {p.thumb_url && (
                      <img
                        src={p.thumb_url}
                        alt=""
                        className={`w-full h-full object-cover ${isRejected ? "opacity-30 grayscale" : ""}`}
                      />
                    )}
                    {isRejected && (
                      <div className="absolute top-1 left-1 bg-destructive text-destructive-foreground text-[10px] px-1.5 py-0.5 rounded font-medium uppercase">
                        Retirée
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                      <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => setCover(p)} title="Définir comme couverture">
                        <Star className="h-3 w-3" />
                      </Button>
                      <Button size="icon" variant="destructive" className="h-7 w-7" onClick={() => deletePhoto(p)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminGalleryEditor;
